import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import { PackingListItem } from '../schema';
import { calculateAvailability } from '../utils/inventory';
import { useState, useEffect } from 'react';
import { db } from '../instant';

interface Props {
    packingListItem: PackingListItem;
    eventId: string;
    allItemLocations: Array<{
        id: string;
        quantity: number;
        itemType?: { id: string } | null;
        event?: { id: string } | null;
    }>;
    disabled?: boolean;
    /** Explains why controls are disabled (e.g. active packing operation). */
    disabledReason?: string;
    onRemove: () => void;
    onPackingListChanged?: () => void;
    onRecordLostOrBroken?: (itemTypeId: string, eventId: string) => void;
}

export default function PackingListItemRow({
    packingListItem,
    eventId,
    allItemLocations,
    disabled = false,
    disabledReason,
    onRemove,
    onPackingListChanged,
    onRecordLostOrBroken,
}: Props) {
    const itemType = packingListItem.itemType;
    const category = itemType?.category;
    const requiredQty = packingListItem.quantity || 0;
    const [localQuantity, setLocalQuantity] = useState(String(requiredQty));
    const [isSaving, setIsSaving] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    // Sync local quantity when packingListItem changes
    useEffect(() => {
        setLocalQuantity(String(requiredQty));
    }, [requiredQty]);

    // Calculate availability (excluding items already at this event)
    const availability = itemType
        ? calculateAvailability(itemType.id, requiredQty, allItemLocations, eventId)
        : { available: 0, required: requiredQty, shortage: requiredQty, status: 'insufficient' as const };

    const { available: availableQty, status, shortage } = availability;

    // Status icon and color
    const statusConfig = {
        sufficient: {
            icon: <CheckCircleIcon sx={{ color: 'success.main' }} />,
            textColor: 'success.main',
            tooltip: `Available: ${availableQty} (sufficient)`,
        },
        low: {
            icon: <WarningIcon sx={{ color: 'warning.main' }} />,
            textColor: 'warning.main',
            tooltip: `Available: ${availableQty}, Required: ${requiredQty}, Only ${Math.round((availableQty / requiredQty) * 100)}% available`,
        },
        insufficient: {
            icon: <ErrorIcon sx={{ color: 'error.main' }} />,
            textColor: 'error.main',
            tooltip: `Available: ${availableQty}, Required: ${requiredQty}, Short by: ${shortage}`,
        },
    };

    const config = statusConfig[status];
    const categoryColor = category?.color || '#2196F3';
    const lockTipProps =
        disabled && disabledReason
            ? { title: disabledReason, disableHoverListener: false as const }
            : { title: '', disableHoverListener: true as const };

    return (
        <TableRow>
            {/* Photo */}
            <TableCell>
                <Avatar
                    src={itemType?.photoUrl || undefined}
                    variant="rounded"
                    sx={{
                        width: 48,
                        height: 48,
                        bgcolor: 'grey.200',
                    }}
                >
                    {!itemType?.photoUrl && <LocalOfferIcon />}
                </Avatar>
            </TableCell>

            {/* Name + Category */}
            <TableCell>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {itemType?.name || 'Unknown Item'}
                        </Typography>
                        {itemType?.consumable && <LocalGasStationIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                    </Box>
                    {category && (
                        <Chip
                            label={category.name}
                            size="small"
                            sx={{
                                bgcolor: categoryColor,
                                color: 'white',
                                fontSize: '0.7rem',
                                width: 'fit-content',
                            }}
                        />
                    )}
                </Box>
            </TableCell>

            {/* Required Quantity Input */}
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip {...lockTipProps}>
                        <Box component="span" sx={{ display: 'inline-block' }}>
                            <TextField
                                type="number"
                                value={localQuantity}
                                disabled={disabled}
                                onChange={e => {
                                    setLocalQuantity(e.target.value);
                                    setError(null);
                                }}
                                onBlur={async () => {
                                    if (disabled) return;
                                    const val = parseInt(localQuantity, 10);
                                    if (isNaN(val) || val < 1) {
                                        setLocalQuantity(String(requiredQty)); // Revert
                                        return;
                                    }

                                    if (val === requiredQty) {
                                        setLocalQuantity(String(requiredQty)); // Normalize display
                                        return; // No change
                                    }

                                    setIsSaving(true);
                                    setError(null);

                                    try {
                                        await db.transact([
                                            db.tx.packingListItems[packingListItem.id].update({
                                                quantity: val,
                                            }),
                                        ]);
                                    } catch (err) {
                                        console.error('Error updating packing list item:', err);
                                        setError('Failed to update quantity');
                                        setLocalQuantity(String(requiredQty)); // Revert on error
                                    } finally {
                                        setIsSaving(false);
                                    }
                                }}
                                inputProps={{
                                    min: 1,
                                    style: { textAlign: 'center', width: '60px' },
                                }}
                                size="small"
                                sx={{ width: '80px' }}
                                error={!!error}
                                helperText={error}
                            />
                        </Box>
                    </Tooltip>
                    {isSaving && <CircularProgress size={16} />}
                </Box>
            </TableCell>

            {/* Available Quantity */}
            <TableCell>
                <Typography variant="body2" sx={{ color: config.textColor, fontWeight: 'medium' }}>
                    {availableQty}
                </Typography>
            </TableCell>

            {/* Status/Warning */}
            <TableCell>
                <Tooltip title={config.tooltip}>{config.icon}</Tooltip>
            </TableCell>

            {/* Actions */}
            <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Tooltip {...lockTipProps}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={disabled}
                                onClick={e => {
                                    e.stopPropagation();
                                    setMenuAnchor(e.currentTarget);
                                }}
                            >
                                <MoreVertIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip {...lockTipProps}>
                        <span>
                            <IconButton size="small" color="error" disabled={disabled} onClick={() => setDeleteDialogOpen(true)}>
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </TableCell>

            {/* Actions Menu */}
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                {onRecordLostOrBroken && itemType && (
                    <MenuItem
                        onClick={() => {
                            setMenuAnchor(null);
                            onRecordLostOrBroken(itemType.id, eventId);
                        }}
                    >
                        <WarningAmberIcon sx={{ mr: 1, fontSize: 20 }} />
                        Lost / Broken
                    </MenuItem>
                )}
            </Menu>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Remove from Packing List?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Remove &quot;{itemType?.name || 'Unknown Item'}&quot; from the packing list? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} autoFocus>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        onClick={async () => {
                            try {
                                await db.transact([db.tx.packingListItems[packingListItem.id].delete()]);
                                setDeleteDialogOpen(false);
                                onRemove();
                                onPackingListChanged?.();
                            } catch (err) {
                                console.error('Error deleting packing list item:', err);
                                // TODO: Show error toast
                            }
                        }}
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </TableRow>
    );
}
